import React from 'react'
import { SaleAgreementPromo, SiteInfo } from './types'
import { ButtonBase, List, ListItem, Stack } from '@mui/material'
import TextField from '../../../../components/TextField'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { PadLineItem } from '../../../../components/Pad/Pad'
import { colors } from '../../../../themes/colors'
import { useCampaignContext } from './CampaignContext'
import { getSites } from './utils'
import Typography from '../../../../components/Typography'
import { ModalActions } from '../../../../components/Modal'
import { Button } from '../../../../components/Button'
import TestIds from '../../../../testIds'

interface PickupSiteProps {
  config: SaleAgreementPromo
}

const PickupSite = ({ config }: PickupSiteProps) => {
  const { state, setState } = useCampaignContext()
  const sites = React.useMemo(() => getSites(config.products), [config.products])

  function handleSiteSelect(site: SiteInfo) {
    setState({
      siteSelected: site,
    })
  }

  function handleReselectSite() {
    setState({
      siteSelected: undefined,
    })
  }

  function handleSubmitNextStep() {
    if (state.siteSelected === undefined) {
      return
    }

    setState({
      tabIndex: state.tabIndex + 1,
    })
  }

  if (state.siteSelected === undefined) {
    return <SelectSiteView sites={sites} onSiteSelect={handleSiteSelect} />
  }

  return (
    <Stack direction='column'>
      <Stack
        direction='row'
        justifyContent='space-between'
        gap={0.5}
        bgcolor='white'
        border={'1px solid #D5D6DA'}
        px={2}
        py={1.5}
        borderRadius={1}
      >
        <Stack direction='row' gap={1}>
          <Typography variant='body2' color='primary500' fontWeight='bold'>
            Site
          </Typography>
          <Typography variant='body2' color='grey800'>
            {state.siteSelected?.siteName}
          </Typography>
        </Stack>
        <ButtonBase
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            alignSelf: 'start',
          }}
          disableRipple
          onClick={handleReselectSite}
        >
          <Typography variant='body2' color='primary500'>
            Edit
          </Typography>
          <MaterialSymbol sx={{ fontSize: '16px', color: colors.primary500 }}>edit</MaterialSymbol>
        </ButtonBase>
      </Stack>

      <ModalActions showDivider>
        <Button
          key='next-step-btn'
          variant='contained'
          onClick={handleSubmitNextStep}
          data-testid={TestIds.feat.campaign.nextButton}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next Step
        </Button>
      </ModalActions>
    </Stack>
  )
}

interface SelectSiteViewProps {
  sites: SiteInfo[]
  onSiteSelect?: (site: SiteInfo) => void
}

export const SelectSiteView: React.FC<SelectSiteViewProps> = ({ sites, onSiteSelect }) => {
  const [siteText, setSiteText] = React.useState('')

  const searchSiteResult = React.useMemo(() => {
    if (siteText.length > 1) {
      return sites.filter((site) => site.siteName.toLowerCase().includes(siteText.toLowerCase()))
    }
    return sites
  }, [siteText, sites])

  return (
    <Stack direction='column' gap={2}>
      <TextField
        label={'Search Site:'}
        value={siteText}
        placeholder='Search site by name'
        testId={TestIds.feat.campaign.components.siteSelector}
        variant={'outlined'}
        onChange={(e) => setSiteText(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        autoFocus={true}
        inputProps={{
          maxLength: 150,
        }}
        InputProps={{
          endAdornment: <MaterialSymbol sx={{ color: colors.primary500 }}>search</MaterialSymbol>,
        }}
      />
      <List sx={{ p: 0, gap: 0 }}>
        {searchSiteResult.map((site) => (
          <ListItem key={site.siteId} sx={{ m: 0, p: 0 }}>
            <PadLineItem
              sx={{ justifyContent: 'space-between' }}
              data-testid='magnesium-site-search-site-id'
              option={{
                title: site.siteName,
                subtitle: '',
              }}
              onClick={() => (onSiteSelect ? onSiteSelect(site) : undefined)}
            />
          </ListItem>
        ))}
      </List>
    </Stack>
  )
}

export default PickupSite
